import { api } from 'api';
import { getArticle, getEnv } from 'common/selectors';
import log from 'loglevel';
import { call, cancelled, put, select, takeLatest } from 'redux-saga/effects';
import { EffectR } from 'types/utils';
import {
  FETCH_REVIEWS_PAGE,
  FETCH_REVIEWS_NEXT_PAGE,
  REVIEWS_SORTING_SET,
  ADD_FILTER_BY_SCORE,
  REMOVE_FILTER_BY_SCORE,
} from '../actions';
import {
  getReviewsByScoreFilters,
  getReviewsPerPage,
  getReviewsProductId,
  getReviewsSorting,
} from '../selectors';
import { ReviewsRequestProps, ReviewsPaginationProps, ReviewsByScoreFilterRecord } from '../types';
import { fetchReviewsPage } from '../utils/api';

export const buildFilterParamsString = (filtersRecords: ReviewsByScoreFilterRecord[]) => {
  return filtersRecords
    .filter((r) => r.isSet)
    .map((r) => `rating:eq:${r.score}`)
    .join(',');
};

export function* runReviewsPageRefetch() {
  const abortController = yield* getAbortController();
  const productId = (yield select(getArticle)) as EffectR.Select<typeof getArticle>;
  const sort = (yield select(getReviewsSorting)) as EffectR.Select<typeof getReviewsSorting>;
  const filtersRecords = (yield select(getReviewsByScoreFilters)) as EffectR.Select<
    typeof getReviewsByScoreFilters
  >;
  const filter = (yield call(buildFilterParamsString, filtersRecords)) as EffectR.Call<
    typeof buildFilterParamsString
  >;

  const fetchProps: ReviewsRequestProps = {
    productId,
    sort,
    filter: filter.length > 0 ? filter : undefined,
  };

  yield put(FETCH_REVIEWS_PAGE.request());
  const { requestID } = (yield select(getEnv)) as EffectR.Select<typeof getEnv>;
  yield call(log.info, 'Product-reviews was requested', 'requestID: ', requestID, 'dateNow: ', Date.now());

  try {
    const data = (yield call(fetchReviewsPage, fetchProps, {
      cancelToken: abortController.token,
    })) as EffectR.Call<typeof fetchReviewsPage>;

    yield put(FETCH_REVIEWS_PAGE.success(data));
    yield call(log.info, 'Product-reviews was loaded', 'requestID: ', requestID, 'dateNow: ', Date.now());
  } catch (e) {
    yield put(FETCH_REVIEWS_PAGE.failure(e as Error));
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    if (e?.response?.status >= 500) {
      yield call(
        log.info,
        'Product-reviews has error',
        'requestID: ',
        requestID,
        'dateNow: ',
        Date.now(),
        'e',
        e,
      );
    }
  } finally {
    if ((yield cancelled()) as boolean) {
      abortController.cancel();
      yield call(
        log.info,
        'Product-reviews was cancelled',
        'requestID: ',
        requestID,
        'dateNow: ',
        Date.now(),
      );
      yield put(FETCH_REVIEWS_PAGE.cancel());
    }
  }
}

export function* runReviewsPaginationNextFetch(props: ReviewsPaginationProps) {
  const productId = (yield select(getReviewsProductId)) as EffectR.Select<typeof getReviewsProductId>;
  const { currentPage } = props;

  if (typeof productId === 'string') {
    const sort = (yield select(getReviewsSorting)) as EffectR.Select<typeof getReviewsSorting>;
    const filtersRecords = (yield select(getReviewsByScoreFilters)) as EffectR.Select<
      typeof getReviewsByScoreFilters
    >;

    const perPage =
      ((yield select(getReviewsPerPage)) as EffectR.Select<typeof getReviewsPerPage>) ?? undefined;

    const filter = (yield call(buildFilterParamsString, filtersRecords)) as EffectR.Call<
      typeof buildFilterParamsString
    >;

    const fetchProps: ReviewsRequestProps = {
      productId,
      page: currentPage + 1,
      perPage,
      sort,
      filter: filter.length > 0 ? filter : undefined,
    };

    const abortController = yield* getAbortController();

    try {
      const data = (yield call(fetchReviewsPage, fetchProps, {
        cancelToken: abortController.token,
      })) as EffectR.Call<typeof fetchReviewsPage>;

      yield put(FETCH_REVIEWS_NEXT_PAGE.success(data));
    } catch (err) {
      if (err instanceof Error) {
        yield put(FETCH_REVIEWS_NEXT_PAGE.failure(err));
      }
    } finally {
      if ((yield cancelled()) as boolean) {
        abortController.cancel();
        yield put(FETCH_REVIEWS_NEXT_PAGE.cancel());
      }
    }
  } else {
    yield put(FETCH_REVIEWS_NEXT_PAGE.failure(new TypeError('Failed to extract productId from reviews')));
  }
}

export function* reviewsSaga() {
  yield takeLatest(FETCH_REVIEWS_NEXT_PAGE.request, ({ payload }) => runReviewsPaginationNextFetch(payload));
}

export function* reviewsFacetsAndSortingsSaga() {
  yield takeLatest(
    [REVIEWS_SORTING_SET, ADD_FILTER_BY_SCORE, REMOVE_FILTER_BY_SCORE],
    runReviewsPageRefetch,
  );
}

function* getAbortController() {
  return (yield call([api, 'abortController'])) as EffectR.Call<(typeof api)['abortController']>;
}
